// React
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// Material UI
import { Grid } from "@mui/material";
// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import useLotsSubscription from "@/Settings/hooks/useLotsSubscription";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// API
import { getAdministrationTools } from "@/Settings/api/AdministrationTools/queries";
import { ADMINISTRATION_TOOLS_SUBSCRIPTION } from "@/Settings/api/AdministrationTools/subscriptions";

// Helpers
import { and } from "@Common/helpers/sonar";

// Components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import AdministrationToolsDealerInfo from "./AdministrationToolsDealerInfo.tsx";
import AdministrationToolsPartnerIntegrations from "./AdministrationToolsPartnerIntegrations";
import AdministrationToolsSideBar from "@/Layouts/components/SideBars/AdministrationToolsSideBar/AdministrationToolsSideBar";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Styles
import bodyClasses from "@/assets/styles/settings/administrationToolsContainer.module.css";

// Types
import { IAdministrationTools } from "@Settings/types/administrationTools/model";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";

const QUERY_IDENTIFIERS = [
	"administrationTools",
	"MetaLastVersionOemsInfo",
	"MetaLastVersionRegionsInfo",
	"inventoryMapping",
	"inventorySettingsData",
	"inventoryMergedData",
	"storePicturesList",
];

export default function AdministrationToolsContainer() {
	const { getSubscription } = useSubscriptions();
	const { clearCache } = useCacheCleaner();
	// Custom Hooks
	const { addSubscription, deleteSubscription } = useSubscriptions();

	// Global State (Redux Store)
	const locationId = useLocationId();

	// Local State
	const [itemSelected, setItemSelected] = useState<{ elementId: string; yCoordinate: number } | null>(null);

	const queryClient = useQueryClient();

	/******************************************************* QUERIES ********************************************************/
	const {
		data: administrationToolsData,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["administrationTools", locationId],
		queryFn: () => getAdministrationTools(locationId),
		enabled: !!locationId,
		staleTime: Infinity,
	});

	const queryIsFinish = and(!isLoading, !isFetching);

	/***************************************************** SUBSCRIPTIONS ******************************************************/
	const administrationToolsSubscriptions = getSubscription(["administrationToolsSubscriptions", locationId]);
	administrationToolsSubscriptions?.subscriptions?.onUpdateAdministrationTools?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdateAdministrationTools", IAdministrationTools>) => {
			const updatedAdministrationTools = response?.data?.onUpdateAdministrationTools;
			queryClient.setQueryData(["administrationTools", locationId], updatedAdministrationTools);
		}
	);

	/******************************************************* HANDLERS ********************************************************/
	const handleItemClick = (elementId: string, yCoordinate: number) => {
		setItemSelected({ elementId, yCoordinate });
	};

	function updateLotsInCache(response: GraphqlSubscriptionResponse<"onUpdateGpsLotsFromIngestion", IAdministrationTools>) {
		const newLots = response?.data?.onUpdateGpsLotsFromIngestion?.integrations?.globalPositioningService?.lots ?? [];

		queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => ({
			...cachedData,
			integrations: {
				...cachedData.integrations,
				globalPositioningService: {
					...cachedData.integrations.globalPositioningService,
					lots: newLots,
				},
			},
		}));
	}

	useLotsSubscription({ locationId, subscriptionKey: "updatedLots", callback: updateLotsInCache });

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["administrationToolsSubscriptions", locationId],
				[
					{
						context: "onUpdateAdministrationTools",
						query: ADMINISTRATION_TOOLS_SUBSCRIPTION,
						variables: {
							locationId,
						},
					},
				]
			);
		}
		return () => {
			deleteSubscription(["administrationToolsSubscriptions", locationId]);
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [locationId, addSubscription]);

	const getContent = () => {
		return [
			<Grid container key={1} className="my-[30px]">
				<Grid item xs={2}>
					<AdministrationToolsSideBar
						activeDefault={itemSelected?.elementId ?? "Location"}
						tab={"DEALERINFO"}
						itemClick={handleItemClick}
					/>
				</Grid>
				<Grid item xs={10}>
					<AdministrationToolsDealerInfo
						itemSelected={itemSelected || { elementId: "Location", yCoordinate: 0 }}
						queryIsFinish={queryIsFinish}
					/>
				</Grid>
			</Grid>,
			<Grid container key={2} className="my-[30px]">
				<Grid item xs={2}>
					<AdministrationToolsSideBar
						activeDefault={itemSelected?.elementId ?? "PayRollProvider"}
						tab={"PARTNERINTEGRATIONS"}
						itemClick={handleItemClick}
					/>
				</Grid>

				<Grid item xs={10}>
					<AdministrationToolsPartnerIntegrations
						itemSelected={itemSelected || { elementId: "PayRollProvider", yCoordinate: 0 }}
						queryIsFinish={queryIsFinish}
						classes={bodyClasses as unknown as string}
						settings={administrationToolsData as IAdministrationTools}
						itemClick={handleItemClick as (elementId: string, coordinate?: number | undefined) => void}
					/>
				</Grid>
			</Grid>,
		];
	};

	return (
		<BodyLayer>
			<RoundedBox className="!p-0">
				<TabSelectable
					tabs={["Dealer Info", "Partner Integrations"]}
					content={getContent()}
					tabsProps={{ style: { marginTop: "30px" } }}
					tabBarClassName="sticky top-[100px] z-[100] bg-white  rounded-lg"
					onTabChange={() => {
						setItemSelected(null);
					}}
				/>
			</RoundedBox>
		</BodyLayer>
	);
}
